import React, { useCallback, useMemo } from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useGate } from 'statsig-react';
import { faPenToSquare } from '@soundxyz/font-awesome/pro-regular-svg-icons/index.mjs';
import type { PostModalSource } from '@soundxyz/utils/analytics';
import { usePostsEnabled } from '@components/Feed/Post/usePostsEnabled';
import { DropdownMenuItem, MenuItemLabel } from '@components/basics/Dropdowns/GlobalDropdown';
import { Tooltip } from '@components/main-components/tooltip/Tooltip';
import { FeedPostModal } from '@components/modals/FeedPostModal';
import { UnlockFeedModal } from '@components/modals/FeedPostModal/UnlockFeedModal';
import { useAuth } from '@context/auth/base';
import { useConnectedAuth } from '@context/auth/connected';
import { ModalContext, ModalType } from '@context/modal';
import {
  FeedPostModalMainSoundFragmentDoc,
  type FragmentType,
  getFragment,
} from '@graphql/reactQuery';
import { useBackendUserPersistedData } from '@hooks/useBackendUserPersistedData';
import { trackClickEvent } from '@lib/analytics';
import { FEATURE_GATES } from '@utils/constants';
import { DataTestId } from '@utils/test';

export const PostReleaseMenuItem = ({
  releaseFrag,
  source,
}: {
  releaseFrag: FragmentType<FeedPostModalMainSoundFragmentDoc>;
  source: PostModalSource;
}) => {
  const { openModal } = ModalContext.useContainer();
  const { isAuthenticatedWithAPI } = useAuth();
  const { login } = useConnectedAuth();
  const { showPostingDisabledTooltip, postingEnabled } = usePostsEnabled();
  const { value: openPostsEnabled } = useGate(FEATURE_GATES.OPEN_POSTS);
  const release = getFragment(FeedPostModalMainSoundFragmentDoc, releaseFrag);
  const { hasFeedAccess } = useBackendUserPersistedData();

  const handleClick = useCallback(() => {
    if (!isAuthenticatedWithAPI) {
      login();
    } else {
      trackClickEvent({
        event: 'Create Post Clicked',
        properties: {
          sourceComponent: source,
          associationId: release.id,
          associationType: 'Release',
        },
      });
      openModal(ModalType.FEED_POST, {
        body: <FeedPostModal key={release.id} releaseFrag={release} sourceLocation="Song Card" />,
      });
    }
  }, [isAuthenticatedWithAPI, login, openModal, release, source]);

  const newHandleClick = useCallback(() => {
    if (!isAuthenticatedWithAPI) {
      login();
    } else {
      trackClickEvent({
        event: 'Create Post Clicked',
        properties: {
          sourceComponent: source,
          associationId: release.id,
          associationType: 'Release',
        },
      });

      if (postingEnabled && hasFeedAccess.type === 'has-access') {
        openModal(ModalType.FEED_POST, {
          body: <FeedPostModal key={release.id} releaseFrag={release} sourceLocation="Song Card" />,
        });
      } else {
        openModal(ModalType.UNLOCK_FEED_POST, {
          body: (
            <UnlockFeedModal
              key={`unlock-feed-${release.id}`}
              releaseFrag={release}
              sourceLocation="Song Card"
            />
          ),
        });
      }
    }
  }, [
    hasFeedAccess.type,
    isAuthenticatedWithAPI,
    login,
    openModal,
    postingEnabled,
    release,
    source,
  ]);

  const menuItem = useMemo(
    () => (
      <DropdownMenuItem
        disabled={showPostingDisabledTooltip && !openPostsEnabled}
        onClick={openPostsEnabled ? newHandleClick : handleClick}
        data-testid={DataTestId.FEED_POST_MENU_ITEM_DROPDOWN}
      >
        <FontAwesomeIcon icon={faPenToSquare} size="lg" />
        <MenuItemLabel>Post</MenuItemLabel>
      </DropdownMenuItem>
    ),
    [showPostingDisabledTooltip, openPostsEnabled, newHandleClick, handleClick],
  );

  return showPostingDisabledTooltip ? (
    <Tooltip openOnClick={false} disableHoverableContent>
      <Tooltip.Trigger asChild>
        <div>{menuItem}</div>
      </Tooltip.Trigger>
      <Tooltip.StyledContent size="xs" className="max-w-[240px] break-words">
        Only available to select users
      </Tooltip.StyledContent>
    </Tooltip>
  ) : (
    menuItem
  );
};
